import { useState } from "react";
import { useLiveQuery } from "dexie-react-hooks";
import { Link2, RefreshCw, Unlink } from "lucide-react";
import { de, type PartnerConnection } from "@ainkauf/core";
import { partnerConnection } from "@/db/repository";
import { gleicheAb, trenne, verbinde, type PartnerFehler } from "@/lib/partner";
import { vorWieLangeGenau } from "@/lib/relative-time";

/**
 * Einstellungen, Abschnitt "Spaisekammer".
 *
 * Drei Zustände, und alle drei stehen in derselben Karte: nicht verbunden,
 * verbunden, von drüben getrennt. Der dritte ist der zweite mit einem Satz
 * mehr und dem Feld für einen neuen Code: Wer getrennt wurde, will meistens
 * wieder verbinden, nicht erst verstehen.
 *
 * Gelesen wird aus Dexie, geschrieben über die Edge Functions. Die Zeilen der
 * Verbindung kommen über Realtime zurück, deshalb springt "zuletzt
 * abgeglichen" von selbst um.
 */
export function SpaisekammerCard() {
  const verbindung = useLiveQuery(() => partnerConnection(), [], undefined);
  const [code, setCode] = useState("");
  const [laeuft, setLaeuft] = useState<"verbinden" | "abgleichen" | "trennen" | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [hinweis, setHinweis] = useState<string | null>(null);

  // undefined heißt "noch nicht gelesen", null heißt "keine Verbindung".
  if (verbindung === undefined) return null;

  const getrennt = verbindung !== null && verbindung.status === "getrennt";
  const verbunden = verbindung !== null && !getrennt;

  function satzZu(grund: PartnerFehler | null, name: string | null): string {
    switch (grund) {
      case "code_invalid":
        return de.spaisekammer.errorCode;
      case "offline":
        return de.spaisekammer.errorOffline;
      case "schon_verbunden":
        return de.spaisekammer.errorAlready(name ?? de.spaisekammer.origin);
      default:
        return de.spaisekammer.errorGeneric;
    }
  }

  async function verbinden() {
    if (laeuft !== null) return;
    setFehler(null);
    setHinweis(null);
    setLaeuft("verbinden");
    const ergebnis = await verbinde(code);
    setLaeuft(null);
    if (!ergebnis.ok) {
      setFehler(satzZu(ergebnis.fehler, ergebnis.householdName));
      return;
    }
    setCode("");
    if (!ergebnis.webhook) setHinweis(de.spaisekammer.noWebhook);
  }

  async function abgleichen() {
    if (laeuft !== null) return;
    setFehler(null);
    setLaeuft("abgleichen");
    const ergebnis = await gleicheAb();
    setLaeuft(null);
    if (!ergebnis.ok) setFehler(satzZu(ergebnis.fehler, null));
  }

  async function trennen() {
    if (laeuft !== null) return;
    if (!window.confirm(de.spaisekammer.disconnectConfirm)) return;
    setFehler(null);
    setHinweis(null);
    setLaeuft("trennen");
    const ergebnis = await trenne();
    setLaeuft(null);
    if (!ergebnis.ok) setFehler(satzZu(ergebnis.fehler, null));
  }

  return (
    <div className="karte flex flex-col gap-3 px-5 py-5" data-testid="spaisekammer-card" data-state={verbunden ? "verbunden" : getrennt ? "getrennt" : "offen"}>
      {verbunden ? (
        <Verbunden
          verbindung={verbindung}
          laeuft={laeuft}
          onSync={() => void abgleichen()}
          onUnlink={() => void trennen()}
        />
      ) : (
        <>
          {getrennt ? (
            <p className="text-ink" data-testid="spaisekammer-disconnected">
              {de.spaisekammer.disconnected}
            </p>
          ) : (
            <p className="text-sm text-ink-2">{de.spaisekammer.intro}</p>
          )}

          <div className="flex gap-2">
            <input
              value={code}
              onChange={(event) => {
                setCode(event.target.value.toUpperCase().slice(0, 6));
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter") void verbinden();
              }}
              // 16 Pixel Schrift, sonst zoomt iOS beim Tippen in das Feld.
              placeholder={de.spaisekammer.codePlaceholder}
              aria-label={de.spaisekammer.codeLabel}
              autoCapitalize="characters"
              autoCorrect="off"
              spellCheck={false}
              inputMode="text"
              maxLength={6}
              data-testid="spaisekammer-code"
              className="num min-h-[52px] min-w-0 flex-1 rounded-[var(--radius-lg)] bg-paper-2 px-4 text-base font-bold tracking-[0.2em] text-ink outline-none focus:ring-2 focus:ring-accent"
            />
            <button
              type="button"
              onClick={() => void verbinden()}
              disabled={code.trim().length !== 6 || laeuft !== null}
              data-testid="spaisekammer-connect"
              className="inline-flex min-h-[52px] shrink-0 items-center gap-2 rounded-[var(--radius-lg)] bg-accent px-5 font-semibold text-white transition-transform active:scale-[0.98] disabled:opacity-40"
            >
              <Link2 size={18} strokeWidth={1.75} aria-hidden />
              {laeuft === "verbinden" ? de.spaisekammer.connecting : de.spaisekammer.connect}
            </button>
          </div>
        </>
      )}

      {fehler !== null && (
        <p className="text-sm font-semibold text-accent" data-testid="spaisekammer-error" role="status">
          {fehler}
        </p>
      )}
      {hinweis !== null && (
        <p className="text-sm text-ink-2" data-testid="spaisekammer-hint" role="status">
          {hinweis}
        </p>
      )}
    </div>
  );
}

function Verbunden({
  verbindung,
  laeuft,
  onSync,
  onUnlink,
}: {
  verbindung: PartnerConnection;
  laeuft: "verbinden" | "abgleichen" | "trennen" | null;
  onSync: () => void;
  onUnlink: () => void;
}) {
  return (
    <>
      <div>
        <p className="font-display text-lg font-extrabold tracking-tight text-ink" data-testid="spaisekammer-household">
          {de.spaisekammer.connectedWith(verbindung.household_name)}
        </p>
        <p className="num text-sm text-ink-2" data-testid="spaisekammer-status">
          {de.spaisekammer.recipeCount(verbindung.recipe_count)},{" "}
          {verbindung.last_sync_at === null ? de.spaisekammer.neverSynced : de.spaisekammer.lastSync(vorWieLangeGenau(verbindung.last_sync_at))}
        </p>
      </div>

      {verbindung.last_error !== null && (
        <p className="text-sm text-ink-2" data-testid="spaisekammer-last-error">
          {verbindung.last_error}
        </p>
      )}

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={onSync}
          disabled={laeuft !== null}
          data-testid="spaisekammer-sync"
          className="inline-flex min-h-[44px] items-center gap-2 rounded-full bg-paper-2 px-4 text-sm font-semibold text-ink-2 disabled:opacity-40"
        >
          <RefreshCw size={16} strokeWidth={1.75} aria-hidden className={laeuft === "abgleichen" ? "animate-spin" : ""} />
          {laeuft === "abgleichen" ? de.spaisekammer.syncing : de.spaisekammer.syncNow}
        </button>
        <button
          type="button"
          onClick={onUnlink}
          disabled={laeuft !== null}
          data-testid="spaisekammer-unlink"
          className="inline-flex min-h-[44px] items-center gap-2 rounded-full px-4 text-sm font-semibold text-accent disabled:opacity-40"
        >
          <Unlink size={16} strokeWidth={1.75} aria-hidden />
          {de.spaisekammer.disconnect}
        </button>
      </div>
    </>
  );
}
